<template>
  <div id="msg-container" :class="[props.msg.role == 'user' ? 'row-reverse' : '']">
    <div>
      <div class="msg-logo" :class="[props.msg.role == 'user' ? 'logo-right' : 'logo-left']">
        <div v-if="msg.role == 'user'" class="user-avatar"></div>
        <img v-else-if="msg.user_info && msg.user_info.profile_photo" :src="msg.user_info.profile_photo" :alt="msg.user_info.first_name || 'Sender'"/>
        <div v-else-if="msg.user_info && !msg.user_info.profile_photo" class="initial-avatar">{{ userInitial }}</div>
        <div v-else class="chat-head-wrapper">
          <img src="../assets/chat_head.svg"/>
        </div>
      </div>
    </div>
    <div id="txt-container" :class="[props.msg.role == 'user' ? 'align-right pad-left' : 'pad-right']">
      <div class="message-content" v-html="renderedContent"></div>
      <div style="align-self: normal;">
      <slot></slot>
      <ContactForm v-if="msg.role == 'user' && (msg.action == 'submit_contact_info' || msg.action == 'update_contact_info')" :inputMode="'phone'" :initialData="msg.contactInfo" :edit="false"></ContactForm>
      <ContactForm v-if="props.msg.type == 'show_contact_form' && canShowContactForm" :inputMode="'phone'"></ContactForm>
      </div>
      <div class="msg-info medium-reg">
        <span>{{ props.msg.role == 'user' ? 'You' : getMessageSignature() }}</span>
        <div class="dot"></div>
        <span>{{ extractTime() }}</span>
      </div>
      <div v-if="props.msg.quick_responses && props.msg.quick_responses.length > 0" class="quick-responses">
        <button
          v-for="response in props.msg.quick_responses"
          :key="response"
          type="button"
          class="quick-response-pill p-small-semibold"
          @click="sendQuickResponse(response)"
        >
          {{ response }}
        </button>
      </div>
    </div>
  </div>
</template>
      
<script setup>
import { marked } from 'marked';
import { computed } from 'vue';
import { chatMessageMarkedRenderer } from '../utils/markedRenderer';
import { useMessagesStore } from '@/stores/useMessagesStore';
import ContactForm from './ContactForm.vue';

const props = defineProps({
  msg: {
    type: Object,
    required: true
  },
  messageSignature: {
    type: String,
    required: true
  },
  canShowContactForm: {
    type: Boolean,
    default: false
  }
})

// First letter from user's first name, fallback to '?'
const userInitial = computed(() => {
  const first = props.msg?.user_info?.first_name || ''
  return first ? first.charAt(0).toUpperCase() : '?'
})

// Computed property to render markdown content
const renderedContent = computed(() => {
  try {
    let html = marked.parse(props.msg.content ?? '', {
      breaks: true,
      gfm: true,
      renderer: chatMessageMarkedRenderer,
    });
    
    // Wrap tables in a scrollable container
    html = html
      .replace(/<table>/g, '<div class="table-wrapper"><table>')
      .replace(/<\/table>/g, '</table></div>')
      .replace(/<th>/g, '<th><div>')
      .replace(/<\/th>/g, '</div></th>')
      .replace(/<td>/g, '<td><div>')
      .replace(/<\/td>/g, '</div></td>');
    
    return html;
  } catch (error) {
    console.error('Error parsing markdown:', error);
    return props.msg.content;
  }
});

const { sendMsg } = useMessagesStore();

function sendQuickResponse(response) {
  if (!response || typeof response !== 'string') return;
  sendMsg(response);
}

// Get message signature - use senderName if available, otherwise fall back to messageSignature prop
function getMessageSignature() {
  return props.msg.user_info?.first_name || props.messageSignature;
}

// Format time for message timestamp
function extractTime() {
  const date = new Date(props.msg.timestamp)
  let hour = date.getHours()
  let mins = date.getMinutes()
  const ampm =  hour >= 12 ? 'PM' : 'AM'
  hour = hour % 12
  hour = hour ? hour : 12
  mins = mins < 10 ? '0'+mins : mins
  return `${hour}:${mins} ${ampm}`
}
</script>
  
<style scoped>
  #msg-container {
    display: flex;
    width: 100%;
    margin-bottom: 40px;
  }

  .row-reverse {
    flex-direction: row-reverse;
  }

  .msg-logo {
    width: 34px;
    height: 34px;
    box-sizing: border-box;
    margin-top: 5px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--white-dark);
    overflow: hidden;
  }

  .msg-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    max-width: 100%;
    max-height: 100%;
  }

  .chat-head-wrapper {
    padding: 3px;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .chat-head-wrapper img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .logo-right {
    margin-left: 12px;
    background-color: #0156FE;
  }

  .user-avatar {
    background: transparent;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    border: 2px solid var(--white-dark);
  }

  .logo-left {
    margin-right: 12px;
  }

  .initial-avatar {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--white-dark);
    color: var(--black-default);
    font-weight: 600;
    font-size: 16px;
  }

  #txt-container {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    max-width: 100%;
    min-width: 0; /* Keep this to ensure proper flex behavior */
  }

  .align-right {
    align-items: flex-end;
  }

  .pad-left {
    padding-left: 46px;
  }

  .pad-right {
    padding-right: 46px;
  }

  .msg-info {
    display: flex;
    align-items: center;
    color: var(--gray-dark);
  }

  .dot {
    width: 3px;
    height: 3px;
    background-color: var(--gray-dark);
    border-radius: 1.5px;
    margin-right: 9px;
    margin-left: 9px;
  }

  .message-content {
    line-height: 1.5;
    width: auto;
    margin-top: 1px;
    min-height: 45px;
    color: var(--black-default);
    overflow-wrap: anywhere;
    word-break: break-word;
    hyphens: auto;
  }

  .message-content :deep(> *:first-child) {
    margin-top: 0;
  }

  .message-content :deep(.table-wrapper) {
    width: 100%;
    overflow-x: auto;
    margin: 0.5em 0;
  }

  .message-content :deep(table) {
    border-collapse: collapse;
    margin: 0;
    width: max-content;
  }

  .message-content :deep(table th),
  .message-content :deep(table td) {
    border: 1px solid var(--gray-light);
    padding: 8px 12px;
    text-align: left;
  }

  .message-content :deep(table th > div),
  .message-content :deep(table td div) {
    display: -webkit-box;
    max-width: 320px;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    -webkit-line-clamp: 5;
    line-clamp: 5;
    -webkit-box-orient: vertical;
    box-orient: vertical;
    overflow: hidden;
    min-height: 0;
  }

  .message-content :deep(table th) {
    background-color: var(--white-dark);
    font-weight: 600;
  }

  .message-content :deep(table tr:nth-child(even)) {
    background-color: var(--white-dark);
  }

  .message-content :deep(code) {
    background-color: var(--white-dark);
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: monospace;
  }

  .message-content :deep(pre) {
    background-color: var(--white-dark);
    padding: 1em;
    border-radius: 5px;
    overflow-x: auto;
    margin: 0.5em 0;
  }

  .message-content :deep(a) {
    color: var(--primary-accent-base);
    text-decoration: underline;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .message-content :deep(a:hover) {
    text-decoration: underline;
  }

  .message-content :deep(img) {
    max-width: 100%;
    height: auto;
  }

  .message-content :deep(blockquote) {
    border-left: 3px solid var(--gray-light);
    margin: 0.5em 0;
    padding-left: 1em;
    color: var(--gray-dark);
  }

  .quick-responses {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
    align-self: flex-start;
  }

  .quick-response-pill {
    font-family: 'Roboto', sans-serif;
    background-color: var(--white-default);
    color: var(--black-default);
    padding: 8px 14px;
    border-radius: 999px;
    border: 1.5px solid var(--primary-accent-base);
    cursor: pointer;
    box-shadow: 0 4px 10px 0 rgba(0,0,0,0.08);
    max-width: 320px;
    text-align: left;
  }

  .quick-response-pill:hover {
    background-color: var(--gray-light);
  }

  .quick-response-pill:active {
    background-color: var(--white-dark);
    border-color: var(--primary-accent-dark);
  }

  .quick-response-pill:focus-visible {
    outline: 2px solid var(--primary-accent-light);
    outline-offset: 2px;
  }
</style>